import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { nsConfirm } from '../shared/ns-confirm'
import {
  Shield,
  ShieldCheck,
  ShieldX,
  Save,
  RotateCcw,
  Settings,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'

const ClientPermissions = ({ clientId }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [localPermissions, setLocalPermissions] = useState({})
  const [showDetails, setShowDetails] = useState(false)
  const queryClient = useQueryClient()

  const { data: permissionData, isLoading, error } = useQuery(
    ['client-permissions', clientId],
    () => apiService.get(`/permissions/client/${clientId}`).then(res => res.data),
    { enabled: !!clientId, refetchOnWindowFocus: false }
  )

  const { data: availablePermissions } = useQuery(
    'available-permissions',
    () => apiService.get('/permissions/available').then(res => res.data),
    { refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000 }
  )

  const updatePermissionsMutation = useMutation(
    (permissions) => apiService.put(`/permissions/client/${clientId}`, { permissions }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-permissions', clientId])
        setIsEditing(false)
        toast.success('Permissions updated successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update permissions')
      }
    }
  )

  const resetPermissionsMutation = useMutation(
    (preset) => apiService.post(`/permissions/client/${clientId}/reset`, { preset }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['client-permissions', clientId])
        setIsEditing(false)
        toast.success(`Permissions reset to ${data.data.preset} preset`)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to reset permissions')
      }
    }
  )

  const startEditing = () => { setLocalPermissions({ ...permissionData.permissions }); setIsEditing(true) }
  const cancelEditing = () => { setLocalPermissions({}); setIsEditing(false) }
  const handlePermissionChange = (permission, value) => {
    setLocalPermissions({ ...localPermissions, [permission]: value })
  }
  const savePermissions = () => updatePermissionsMutation.mutate(localPermissions)
  const resetToPreset = async (preset) => {
    const ok = await nsConfirm({
      tone: 'warning',
      icon: 'shield',
      title: `Reset to ${preset} preset?`,
      message: 'All current permission settings will be overwritten with the preset defaults.',
      confirmLabel: 'Reset permissions',
    })
    if (ok) resetPermissionsMutation.mutate(preset)
  }

  const getCategoryColor = (category) => {
    const map = {
      'Content': { bg: 'rgba(108,99,255,0.1)', color: 'var(--accent)' },
      'Site Management': { bg: 'rgba(34,197,94,0.1)', color: 'var(--success)' },
      'Analytics': { bg: 'rgba(108,99,255,0.1)', color: 'var(--accent)' },
      'Support': { bg: 'rgba(245,158,11,0.1)', color: 'var(--warning)' },
      'Billing': { bg: 'rgba(239,68,68,0.1)', color: 'var(--danger)' },
      'Data': { bg: 'var(--surface-2)', color: 'var(--text-soft)' }
    }
    return map[category] || map['Data']
  }

  if (isLoading) {
    return (
      <div className="card card-pad">
        <div className="flex items-center" style={{ gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{ padding: '0.5rem', borderRadius: 'var(--radius)', background: 'var(--surface-2)' }}>
            <Shield style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
          </div>
          <span className="font-semibold">Client Permissions</span>
        </div>
        <div className="flex items-center justify-center" style={{ padding: '2rem 0' }}>
          <LoadingSpinner size="md" text="Loading permissions..." />
        </div>
      </div>
    )
  }

  if (error || !permissionData) {
    return (
      <div className="card card-pad">
        <div className="flex items-center" style={{ gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{ padding: '0.5rem', borderRadius: 'var(--radius)', background: 'var(--surface-2)' }}>
            <Shield style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
          </div>
          <span className="font-semibold">Client Permissions</span>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <AlertCircle style={{ width: '3rem', height: '3rem', color: 'var(--danger)', margin: '0 auto 1rem' }} />
          <p className="text-muted">Failed to load permissions</p>
        </div>
      </div>
    )
  }

  const permissions = isEditing ? localPermissions : permissionData.permissions
  const groupedPermissions = availablePermissions?.permissionsByCategory || {}

  return (
    <div className="card card-pad">
      {/* Header */}
      <div className="flex items-center justify-between" style={{ marginBottom: '1.5rem' }}>
        <div className="flex items-center" style={{ gap: '0.75rem' }}>
          <div style={{ padding: '0.5rem', borderRadius: 'var(--radius)', background: 'var(--surface-2)' }}>
            <Shield style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
          </div>
          <span className="font-semibold">Client Permissions</span>
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => setShowDetails(!showDetails)}
            title={showDetails ? 'Hide details' : 'Show details'}
          >
            {showDetails ? <EyeOff style={{ width: '1rem', height: '1rem' }} /> : <Eye style={{ width: '1rem', height: '1rem' }} />}
          </button>
        </div>

        <div className="flex items-center" style={{ gap: '0.5rem' }}>
          {!isEditing ? (
            <>
              <button className="btn btn-ghost btn-sm flex items-center gap-1" onClick={() => setShowDetails(!showDetails)}>
                <Settings style={{ width: '1rem', height: '1rem' }} />
                {showDetails ? 'Hide' : 'Details'}
              </button>
              <button className="btn btn-primary btn-sm flex items-center gap-1" onClick={startEditing}>
                <Settings style={{ width: '1rem', height: '1rem' }} />
                Edit Permissions
              </button>
            </>
          ) : (
            <>
              <button
                className="btn btn-ghost btn-sm"
                onClick={cancelEditing}
                disabled={updatePermissionsMutation.isLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm flex items-center gap-1"
                onClick={savePermissions}
                disabled={updatePermissionsMutation.isLoading}
              >
                {updatePermissionsMutation.isLoading
                  ? <LoadingSpinner size="sm" />
                  : <><Save style={{ width: '1rem', height: '1rem' }} />Save Changes</>
                }
              </button>
            </>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div
        className="flex items-center justify-between"
        style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          background: 'var(--surface-2)',
          borderRadius: 'var(--radius)',
          border: '1px solid var(--border)'
        }}
      >
        <div className="flex items-center" style={{ gap: '1rem' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--success)' }}>
              {Object.values(permissions).filter(Boolean).length}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-soft)' }}>Enabled</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--danger)' }}>
              {Object.values(permissions).filter(p => !p).length}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-soft)' }}>Disabled</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div className="font-bold" style={{ fontSize: '1.5rem' }}>
              {Object.keys(permissions).length}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-soft)' }}>Total</div>
          </div>
        </div>

        {isEditing && (
          <div className="flex items-center" style={{ gap: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-soft)' }}>Quick presets:</span>
            {['basic', 'standard', 'full'].map((preset) => (
              <button
                key={preset}
                className="btn btn-ghost btn-sm"
                onClick={() => resetToPreset(preset)}
                disabled={resetPermissionsMutation.isLoading}
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
              >
                {preset === 'full' ? 'Full Access' : preset.charAt(0).toUpperCase() + preset.slice(1)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Permissions List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => {
          const { bg, color } = getCategoryColor(category)
          return (
            <div key={category}>
              <div className="flex items-center" style={{ marginBottom: '0.75rem', gap: '0.5rem' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '0.125rem 0.625rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem', fontWeight: 500,
                  background: bg, color
                }}>
                  {category}
                </span>
                <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                  ({categoryPermissions.filter(p => permissions[p.key]).length}/{categoryPermissions.length} enabled)
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                {categoryPermissions.map((permission) => (
                  <PermissionItem
                    key={permission.key}
                    permission={permission}
                    hasPermission={permissions[permission.key]}
                    isEditing={isEditing}
                    showDetails={showDetails}
                    onChange={(value) => handlePermissionChange(permission.key, value)}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* Help Text */}
      <div style={{
        marginTop: '1.5rem',
        padding: '1rem',
        background: 'var(--accent-soft)',
        borderRadius: 'var(--radius)',
        border: '1px solid var(--accent-3)'
      }}>
        <div className="flex items-start" style={{ gap: '0.5rem' }}>
          <Info style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)', flexShrink: 0, marginTop: '0.125rem' }} />
          <div style={{ fontSize: '0.875rem', color: 'var(--accent)' }}>
            <p className="font-medium" style={{ marginBottom: '0.25rem' }}>Permission Guidelines:</p>
            <ul style={{ paddingLeft: '1rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem' }}>
              <li><strong>Basic:</strong> Limited access - suitable for content reviewers</li>
              <li><strong>Standard:</strong> Most features enabled - suitable for typical clients</li>
              <li><strong>Full Access:</strong> All permissions enabled - use with caution</li>
              <li>Changes take effect immediately and apply to all client dashboard features</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

const getPermissionIcon = (hasPermission) => hasPermission
  ? <CheckCircle style={{ width: '1rem', height: '1rem', color: 'var(--success)' }} />
  : <XCircle style={{ width: '1rem', height: '1rem', color: 'var(--danger)' }} />

const PermissionItem = ({ permission, hasPermission, isEditing, showDetails, onChange }) => (
  <div style={{
    padding: '0.75rem',
    border: `1px solid ${hasPermission ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`,
    borderRadius: 'var(--radius)',
    background: hasPermission ? 'rgba(34,197,94,0.05)' : 'var(--surface-2)',
    transition: 'background 0.15s'
  }}>
    <div className="flex items-start justify-between">
      <div style={{ flex: 1 }}>
        <div className="flex items-center" style={{ gap: '0.5rem' }}>
          {getPermissionIcon(hasPermission)}
          <span className="font-medium" style={{ fontSize: '0.875rem' }}>{permission.name}</span>
        </div>
        {showDetails && (
          <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
            {permission.description}
          </p>
        )}
      </div>
      {isEditing && (
        <div style={{ marginLeft: '0.75rem' }}>
          <input
            type="checkbox"
            checked={hasPermission}
            onChange={(e) => onChange(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
        </div>
      )}
    </div>
  </div>
)

export default ClientPermissions
